import { useState } from 'react'
import { useNavigate } from 'react-router'
import { createCheckoutSession } from '@healthshare/shared/commands/billing'
import { useBackendApi } from '../hooks/useBackendApi'
import { useAsyncEffect } from '../hooks/useAsyncEffect'
import { useSentry } from '../providers/sentry'

export type Plan = {
  price_id: string
  // Known values are 'solo' and 'family'. 'team' is intentionally absent —
  // team plans are billed per employee and never appear in /stripe/plans.
  // We use string rather than a union so the tier guard below stays valid if
  // the backend ever adds a tier we haven't mapped in PLAN_DETAILS.
  tier: string
  trial_period_days?: number
}

// Sentinel value used in place of a Stripe price_id when the user picks the team
// option. Team plans aren't a single Stripe Price (they're billed per covered
// employee), so selecting this routes to the dedicated /team-signup flow instead
// of creating an individual checkout session.
const TEAM_OPTION = 'team'

const PLAN_DETAILS: Record<string, { name: string, description: string, features: string[] }> = {
  solo: {
    name: 'Solo',
    description: 'Full access for you',
    features: [
      'Track all your medical expenses in one place',
      'Submit expenses for yourself',
      'AI-powered assistance from Alice, including phone calls',
      'Link your HSA account',
    ],
  },
  family: {
    name: 'Family',
    description: 'Full access for your whole household',
    features: [
      'Everything in Solo',
      'Submit expenses for your spouse and dependents',
      'Invite your spouse to co-manage your membership',
    ],
  },
  team: {
    name: 'Team',
    description: 'Cover your whole team with a single subscription',
    features: [
      'All Family plan features for every covered employee',
      'One bill, priced per covered employee',
      'Add or remove employees anytime',
    ],
  },
}

interface SubscriptionPlanSelectorProps {
  onSubscribe?: () => void
  successUrl?: string
  cancelUrl?: string
  buttonText?: string
  /** Tier to preselect (e.g. 'family' when the blocked action requires the Family plan) */
  preselectTier?: string
}

export function SubscriptionPlanSelector({
  onSubscribe,
  successUrl = window.location.origin,
  cancelUrl = window.location.href,
  buttonText,
  preselectTier,
}: SubscriptionPlanSelectorProps) {
  const api = useBackendApi()
  const { captureException } = useSentry()
  const navigate = useNavigate()
  const [plans, setPlans] = useState<Plan[]>([])
  const [selectedPriceId, setSelectedPriceId] = useState<string>()
  const [subscribing, setSubscribing] = useState(false)
  const [checkoutError, setCheckoutError] = useState<string>()

  const isTeamSelected = selectedPriceId === TEAM_OPTION

  const { error: plansError } = useAsyncEffect(async () => {
    const r = await fetch('/backend/stripe/plans')
    if (!r.ok) throw new Error(`Failed to load subscription plans (${r.status})`)
    const data = await r.json()
    if (data.plans?.length) {
      setPlans(data.plans)
      // Honor an explicit preselection (e.g. Family when the blocked action
      // requires it), otherwise default to the Family plan.
      const preselected = preselectTier && data.plans.find((p: Plan) => p.tier === preselectTier)
      const family = data.plans.find((p: Plan) => p.tier === 'family')
      setSelectedPriceId(preselected?.price_id || family?.price_id || data.plans[0].price_id)
    }
  }, [preselectTier])

  const handleSubscribe = async () => {
    // Team plans are billed per employee through a separate flow that also
    // collects the organization name and seat count, so hand off to /team-signup
    // rather than creating an individual Stripe checkout session here.
    if (isTeamSelected) {
      onSubscribe?.()
      navigate('/team-signup')
      return
    }

    setSubscribing(true)
    setCheckoutError(undefined)

    try {
      const { url } = await createCheckoutSession(api, {
        successUrl,
        cancelUrl,
        priceId: selectedPriceId!,
      })
      onSubscribe?.()
      window.location.href = url
    } catch (err) {
      captureException(err, { tags: { feature: 'subscription', subscription_op: 'checkout' } })
      setCheckoutError(err instanceof Error ? err.message : 'Something went wrong')
      setSubscribing(false)
    }
  }

  const selectedPlan = plans.find(p => p.price_id === selectedPriceId)
  const trialDays = selectedPlan?.trial_period_days

  return (
    <div>
      {plans.length > 0 && (
        <div className="row mb-4">
          {plans.map(plan => {
            const details = PLAN_DETAILS[plan.tier]
            if (!details || plan.tier === 'team') return null
            const isSelected = selectedPriceId === plan.price_id
            return (
              <div key={plan.price_id} className="col-12 col-md-6 mb-3">
                <div
                  className={`card h-100 ${isSelected ? 'border-primary' : ''}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedPriceId(plan.price_id)}
                >
                  <div className="card-body">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="plan"
                        checked={isSelected}
                        onChange={() => setSelectedPriceId(plan.price_id)}
                      />
                      <label className="form-check-label">
                        <strong>{details.name}</strong>
                        {plan.trial_period_days && (
                          <span className="badge bg-success ms-2">{plan.trial_period_days}-day free trial</span>
                        )}
                      </label>
                    </div>
                    <p className="text-muted mt-2 mb-2 small">{details.description}</p>
                    <ul className="mb-0 small">
                      {details.features.map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            )
          })}

          {/* Team option — peer choice so admins can sign up for a team plan up
              front instead of subscribing individually and upgrading later. */}
          <div className="col-12 col-md-6 mb-3">
            <div
              className={`card h-100 ${isTeamSelected ? 'border-primary' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedPriceId(TEAM_OPTION)}
            >
              <div className="card-body">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="plan"
                    checked={isTeamSelected}
                    onChange={() => setSelectedPriceId(TEAM_OPTION)}
                  />
                  <label className="form-check-label">
                    <strong>{PLAN_DETAILS.team.name}</strong>
                  </label>
                </div>
                <p className="text-muted mt-2 mb-2 small">{PLAN_DETAILS.team.description}</p>
                <ul className="mb-0 small">
                  {PLAN_DETAILS.team.features.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {(plansError || checkoutError) && (
        <div className="alert alert-danger">{checkoutError ?? plansError?.message}</div>
      )}

      <button
        className={`btn btn-primary btn-lg ${subscribing || plans.length === 0 ? 'disabled' : ''}`}
        onClick={handleSubscribe}
        disabled={subscribing || plans.length === 0}
      >
        {subscribing && (
          <span className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </span>
        )}
        {isTeamSelected
          ? 'Set Up Team Plan'
          : (buttonText || (trialDays ? `Start ${trialDays}-Day Free Trial` : 'Subscribe Now'))}
      </button>
    </div>
  )
}
